import React, { useEffect } from 'react';

import { useSelector } from 'react-redux';

import { TranslatedRichText } from '@/components/TranslatedRichText';
import { useIntl } from 'react-intl';
import { useAbsoluteNavigate } from './useAbsoluteNavigate';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import { BffUserGracePeriodResponse, KycType } from '@lucky7ventures/bff-types';
import BffApiService from '@/shared/bffApiService';

const TOAST_ID = 'GRACE_PERIOD_TOAST';

export function useGracePeriodToast(
  addToastCallback: (message: any) => void,
  cleanupToastCallback: (key: string) => void,
) {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const locale = useSelector(selectLocale);
  const absoluteNavigate = useAbsoluteNavigate();
  const intl = useIntl();
  const { request } = useBffApiRequest<BffUserGracePeriodResponse>();

  const handleCtaClick = () => {
    absoluteNavigate('/settings/verify');
  };

  useEffect(() => {
    // Should be triggered only for authenticated Finnish user
    if (!isAuthenticated || !['fi', 'fi-en'].includes(locale)) {
      cleanupToastCallback(TOAST_ID);
      return;
    }

    request({
      apiMethod: BffApiService.fetchGracePeriod,
      successCallback: (data: BffUserGracePeriodResponse) => {
        const gracePeriodData = data.filter(i => i.documentTypeId !== KycType.SourceOfFunds);
        if (gracePeriodData.length > 0) {
          // There could be multiple documents that user needs to send/verify, meaning multiple
          // endDates can be present, but we just take the first document's endDate. We do that
          // because either way, user will end up on the /verify page where user will see all
          // the needed documents that need to be verified.
          const endDateLocaleString = new Date(gracePeriodData[0].endDate).toLocaleDateString();
          const toast = {
            id: TOAST_ID,
            order: 1,
            subtitle: (
              <TranslatedRichText
                id="gracePeriodToast.text"
                values={{ date: endDateLocaleString }}
              />
            ),
            cta: intl.formatMessage({ id: 'gracePeriodToast.cta' }),
            clickCallback: handleCtaClick,
            closeCallback: () => cleanupToastCallback(TOAST_ID),
          };

          addToastCallback(toast);
        } else {
          cleanupToastCallback(TOAST_ID);
        }
      },
      errorCallback: () => {
        cleanupToastCallback(TOAST_ID);
      },
    });
  }, [isAuthenticated, locale]);
}
